import { z } from 'zod';
import { TELEPHONY_CALL_STATUSES, TELEPHONY_CALLS_DELETE_ALL_PHRASE, TELEPHONY_PROVIDERS, } from '../types/telephony-support.js';
/**
 * Telephony Support (UST-3132) validators.
 *
 * Field rules mirror the Sales voice agent's schema so an admin who has
 * configured one recognises the errors from the other.
 */
const apiKeyField = z
    .string()
    .trim()
    .min(20, 'API key looks too short')
    .max(512)
    .refine((s) => !/\s/.test(s), 'API key cannot contain whitespace');
const webhookSecretField = z.string().trim().min(16, 'Secret looks too short').max(512);
/**
 * VoiceInfra's region-specific API host. https only — the API key rides in a
 * header, and http would put it on the wire in clear.
 */
const baseUrlField = z
    .string()
    .trim()
    .max(256)
    .refine((s) => /^https:\/\/[^\s/]+/.test(s), 'Must be an https URL, e.g. https://eu.voiceinfra.ai');
/** E.164, e.g. +14155551234. */
const e164Field = z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{6,14}$/, 'Must be E.164, e.g. +14155551234');
export const TelephonyProviderSchema = z.enum(TELEPHONY_PROVIDERS);
export const PatchTelephonySupportConfigRequestSchema = z
    .object({
    enabled: z.boolean().optional(),
    // Explicit null clears the provider, which is why this is a union rather
    // than `.optional()` alone.
    activeProvider: z.union([TelephonyProviderSchema, z.null()]).optional(),
    topKChunks: z.number().int().min(0).max(20).optional(),
    allowTicketCreation: z.boolean().optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const PatchTelephonyProviderConfigRequestSchema = z
    .object({
    provider: TelephonyProviderSchema,
    apiKey: apiKeyField.optional(),
    agentId: z.union([z.string().trim().min(1).max(256), z.null()]).optional(),
    inboundNumber: z.union([e164Field, z.null()]).optional(),
    webhookSecret: webhookSecretField.optional(),
    /** VoiceInfra only — its API host is region-specific. */
    baseUrl: z.union([baseUrlField, z.null()]).optional(),
})
    .refine((d) => Object.keys(d).length > 1, {
    message: 'At least one field must be provided',
})
    // Storing a host for a provider whose host is fixed would be silently
    // ignored later, which is worse than refusing it now.
    .refine((d) => d.baseUrl === undefined || d.provider === 'voiceinfra', {
    message: 'Only VoiceInfra takes a base URL',
    path: ['baseUrl'],
});
export const VerifyTelephonyProviderRequestSchema = z.object({
    provider: TelephonyProviderSchema,
});
export const TelephonyCallsQuerySchema = z.object({
    status: z.enum(TELEPHONY_CALL_STATUSES).optional(),
    provider: TelephonyProviderSchema.optional(),
    /** Matches caller number or name. */
    search: z.string().trim().min(1).max(200).optional(),
    withTickets: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
});
export const TelephonyCallIdParamSchema = z.object({ callId: z.string().uuid() });
/** Settings → Telephony support → Danger zone: only with the phrase typed exactly. */
export const DeleteAllTelephonyCallsSchema = z.object({
    confirmation: z.literal(TELEPHONY_CALLS_DELETE_ALL_PHRASE, {
        error: `Type ${TELEPHONY_CALLS_DELETE_ALL_PHRASE} to confirm`,
    }),
});
//# sourceMappingURL=telephony-support.schema.js.map